import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Target, Users, TrendingUp, Brain, Heart, Sparkles, ArrowRight } from "lucide-react";

interface IntroToStepProps {
  onComplete: () => void;
}

const IntroToStep = ({ onComplete }: IntroToStepProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Welcome to Step!",
      subtitle: "Your personal wellbeing companion",
      icon: <Sparkles className="h-12 w-12 text-primary" />,
      content: (
        <div className="space-y-4">
          <p className="text-lg text-muted-foreground">
            Step helps you build better work habits, understand your patterns, and maintain sustainable wellbeing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Daily Check-ins</h4>
                <p className="text-sm text-muted-foreground">Track your energy, mood, and progress</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Brain className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-medium">AI Insights</h4>
                <p className="text-sm text-muted-foreground">Get personalized recommendations</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Team Pulse</h4>
                <p className="text-sm text-muted-foreground">Stay connected with your team</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <TrendingUp className="h-5 w-5 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-medium">Progress Tracking</h4>
                <p className="text-sm text-muted-foreground">See your growth over time</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "How It Works",
      subtitle: "Simple daily habits for lasting change",
      icon: <Target className="h-12 w-12 text-primary" />,
      content: (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary">1</span>
              </div>
              <div>
                <h4 className="font-medium mb-1">Daily Check-in (2 minutes)</h4>
                <p className="text-sm text-muted-foreground">
                  Start your day by rating your energy, mood, and setting intentions. It's quick and helps you stay mindful.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary">2</span>
              </div>
              <div>
                <h4 className="font-medium mb-1">AI-Powered Suggestions</h4>
                <p className="text-sm text-muted-foreground">
                  Based on your patterns, get personalized recommendations for breaks, focus time, or team connections.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-primary">3</span>
              </div>
              <div>
                <h4 className="font-medium mb-1">Reflect & Grow</h4>
                <p className="text-sm text-muted-foreground">
                  End your day with a quick reflection. Watch your insights evolve and celebrate your progress.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "The Benefits",
      subtitle: "What you'll gain with Step",
      icon: <Heart className="h-12 w-12 text-primary" />,
      content: (
        <div className="space-y-6">
          <div className="grid gap-4">
            <Card className="border-green-200 dark:border-green-800/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
                    <Brain className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Better Self-Awareness</h4>
                    <p className="text-sm text-muted-foreground">
                      Understand your energy patterns, identify what drives you, and learn when you're most productive.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 dark:border-blue-800/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Sustainable Performance</h4>
                    <p className="text-sm text-muted-foreground">
                      Maintain high performance without burnout by understanding and respecting your limits.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-purple-200 dark:border-purple-800/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                    <Users className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h4 className="font-medium mb-1">Stronger Team Connection</h4>
                    <p className="text-sm text-muted-foreground">
                      Build empathy and support within your team by sharing and understanding each other's wellbeing.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )
    },
    {
      title: "Ready to Start?",
      subtitle: "Let's get you set up for success",
      icon: <ArrowRight className="h-12 w-12 text-primary" />,
      content: (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-4">What happens next?</h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold">1</span>
              </div>
              <div>
                <h4 className="font-medium mb-1">Complete Your Profile</h4>
                <p className="text-sm text-muted-foreground">
                  Tell us about yourself, your skills, and your goals. This helps us personalize your experience.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold">2</span>
              </div>
              <div>
                <h4 className="font-medium mb-1">Take Your First Check-in</h4>
                <p className="text-sm text-muted-foreground">
                  Start with a quick check-in to establish your baseline and get your first personalized recommendations.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold">3</span>
              </div>
              <div>
                <h4 className="font-medium mb-1">Explore the Dashboard</h4>
                <p className="text-sm text-muted-foreground">
                  Navigate between Overview, Insights, and Team tabs to get familiar with all the features.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-primary/10 p-4 rounded-lg">
            <p className="text-sm text-center">
              <strong>Pro tip:</strong> Consistency is key! Even 2 minutes a day of check-ins will help you build powerful insights about your work patterns.
            </p>
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const currentSlideData = slides[currentSlide];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl shadow-xl">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            {currentSlideData.icon}
          </div>
          <CardTitle className="text-3xl font-bold">{currentSlideData.title}</CardTitle>
          <CardDescription className="text-lg">{currentSlideData.subtitle}</CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          <div className="transition-all duration-300 ease-in-out">
            {currentSlideData.content}
          </div>

          <div className="flex items-center justify-between mt-8">
            <Button
              variant="ghost"
              onClick={prevSlide}
              disabled={currentSlide === 0}
            >
              Previous
            </Button>

            <div className="flex gap-2">
              {slides.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    index === currentSlide ? "bg-primary" : "bg-muted-foreground/30"
                  }`}
                />
              ))}
            </div>

            <Button onClick={nextSlide}>
              {currentSlide === slides.length - 1 ? (
                <>
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              ) : (
                "Next"
              )}
            </Button>
          </div>

          {currentSlide === slides.length - 1 && (
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Ready to start your wellbeing journey? Let's set up your profile!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IntroToStep;